import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  Col,
  Dropdown,
  Empty,
  Form,
  Input,
  Layout,
  Menu,
  Modal,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tag,
  Typography,
  notification,
} from "antd";
import {
  AppstoreOutlined,
  BarChartOutlined,
  CopyOutlined,
  FileTextOutlined,
  KeyOutlined,
  LineChartOutlined,
  LogoutOutlined,
  StopOutlined,
  UserOutlined,
} from "@ant-design/icons";
import {
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  Tooltip,
} from "chart.js";
import { Line } from "react-chartjs-2";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import moment from "moment";
import ApiKeyServices from "../../services/api-key.services";
import ApiLogService from "../../services/api-log.services";
import ApiServices from "../../services/api.services";
import AuthService from "../../services/auth.services";
import ProfileServices from "../../services/profile.services";
import { setAuth, setUser } from "../../redux/commonSlice";
import "./portal.css";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend);

const { Header, Sider, Content } = Layout;
const { Title, Text } = Typography;
const BRAND_LOGO_PATH = "/branding/onechain-logo.png";

const normalizePathForTry = (rawPath) => {
  if (!rawPath) return "/";
  const asString = rawPath.trim();
  const withoutHost = asString.replace(/^https?:\/\/[^/]+/i, "");

  if (withoutHost.startsWith("/api/v2")) return withoutHost;
  if (withoutHost.startsWith("/api/")) return withoutHost.replace(/^\/api/, "/api/v2");
  if (withoutHost.startsWith("/")) return `/api/v2${withoutHost}`;

  return `/api/v2/${withoutHost}`;
};

const applyPathParams = (rawPath, bodyValue) => {
  if (!rawPath) return "/";

  const bodyObject = safeParse(bodyValue, {});
  const safeBody = bodyObject && typeof bodyObject === "object" && !Array.isArray(bodyObject) ? bodyObject : {};

  return rawPath.replace(/\{([^}]+)\}/g, (match, paramName) => {
    const value = safeBody[paramName];
    if (value === undefined || value === null || value === "") return match;
    return encodeURIComponent(String(value));
  });
};

const safeParse = (value, fallback = null) => {
  if (!value) return fallback;
  if (typeof value === "object") return value;
  try {
    return JSON.parse(value);
  } catch (error) {
    return fallback;
  }
};

const formatJson = (value) => {
  if (!value) return "{}";
  if (typeof value === "string") {
    const parsed = safeParse(value, null);
    return parsed ? JSON.stringify(parsed, null, 2) : value;
  }
  return JSON.stringify(value, null, 2);
};

const normalizeJsonForDisplay = (value) => {
  const walk = (input) => {
    if (typeof input === "string") {
      const nested = safeParse(input, null);
      return nested === null ? input : walk(nested);
    }

    if (Array.isArray(input)) {
      return input.map((item) => walk(item));
    }

    if (input && typeof input === "object") {
      return Object.fromEntries(Object.entries(input).map(([key, item]) => [key, walk(item)]));
    }

    return input;
  };

  const normalized = walk(value);
  if (typeof normalized === "string") return normalized;
  return JSON.stringify(normalized ?? {}, null, 2);
};

const isEffectivelyEmptyJson = (value) => {
  if (value === null || value === undefined) return true;
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed === "" || trimmed === "{}" || trimmed === "[]";
  }

  if (Array.isArray(value)) return value.length === 0;

  if (typeof value === "object") return Object.keys(value).length === 0;

  return false;
};

const renderJsonBodyBlock = (value, emptyLabel) => {
  if (isEffectivelyEmptyJson(value)) {
    return <Text type="secondary">{emptyLabel}</Text>;
  }

  return (
    <pre style={{ margin: 0, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
      {normalizeJsonForDisplay(value)}
    </pre>
  );
};

const getTryBodyFallback = (endpoint) => {
  const path = String(endpoint?.url || "");
  const paramsSample = safeParse(endpoint?.params, {});
  const fallback = {};

  const placeholderMatches = Array.from(path.matchAll(/\{([^}]+)\}/g)).map((match) => match[1]);
  placeholderMatches.forEach((name) => {
    if (!fallback[name]) fallback[name] = "String";
  });

  const paramKeys = paramsSample && typeof paramsSample === "object" ? Object.keys(paramsSample) : [];
  const querySample = safeParse(endpoint?.query, {});
  const queryKeys = querySample && typeof querySample === "object" ? Object.keys(querySample) : [];
  const bodySample = safeParse(endpoint?.body, {});
  const bodyKeys = bodySample && typeof bodySample === "object" ? Object.keys(bodySample) : [];
  const hasTxid = path.toLowerCase().includes("txid") || paramKeys.some((key) => key.toLowerCase().includes("txid"));
  const hasProductId = path.toLowerCase().includes("productid") || paramKeys.some((key) => key.toLowerCase().includes("productid"));

  if (hasTxid && !fallback.txid) fallback.txid = "String";
  if (hasProductId && !fallback.productId) fallback.productId = "String";

  [...paramKeys, ...queryKeys, ...bodyKeys].forEach((k) => {
    if (!fallback[k]) fallback[k] = "String";
  });

  const nameLower = String(endpoint?.name || "").toLowerCase();
  const descLower = String(endpoint?.description || "").toLowerCase();
  if ((nameLower.includes("txid") || descLower.includes("txid")) && !fallback.txid) fallback.txid = "String";
  if ((nameLower.includes("product") && nameLower.includes("id")) || descLower.includes("product id")) {
    if (!fallback.productId) fallback.productId = "String";
    if (!fallback.id) fallback.id = "String";
  }

  return fallback;
};

const getDocSampleBody = (endpoint) => {
  const parsedBody = safeParse(endpoint?.body, null);
  if (parsedBody && typeof parsedBody === "object" && !Array.isArray(parsedBody) && Object.keys(parsedBody).length > 0) {
    return parsedBody;
  }

  const fallbackBody = getTryBodyFallback(endpoint);
  if (Object.keys(fallbackBody).length > 0) return fallbackBody;

  return {};
};

const buildUsageSeries = (logs, days) => {
  const bucket = {};
  const labels = [];

  for (let i = days - 1; i >= 0; i -= 1) {
    const key = moment().subtract(i, "days").format("YYYY-MM-DD");
    labels.push(moment(key).format("MM/DD"));
    bucket[key] = { req: 0, err: 0, success: 0 };
  }

  logs.forEach((log) => {
    const key = moment(log.createdAt).format("YYYY-MM-DD");
    if (!bucket[key]) return;

    bucket[key].req += 1;
    if (log.status_code >= 400) bucket[key].err += 1;
    if (log.status_code >= 200 && log.status_code < 300) bucket[key].success += 1;
  });

  const keys = Object.keys(bucket).sort();

  return {
    labels,
    requests: keys.map((key) => bucket[key].req),
    errors: keys.map((key) => bucket[key].err),
    success: keys.map((key) => bucket[key].success),
  };
};

const PortalShell = () => {
  const dispatch = useDispatch();
  const currentUser = useSelector((state) => state.common.user);
  const isAdmin = useMemo(() => (currentUser?.roles || []).includes("ROLE_ADMIN"), [currentUser]);

  const [activeKey, setActiveKey] = useState("overview");
  const [loading, setLoading] = useState(false);

  const [allEndpoints, setAllEndpoints] = useState([]);
  const [ownedEndpoints, setOwnedEndpoints] = useState([]);
  const [globalKeys, setGlobalKeys] = useState([]);
  const [logs, setLogs] = useState([]);
  const [myProfile, setMyProfile] = useState(null);
  const [billingConfig, setBillingConfig] = useState(null);

  const [usageWindow, setUsageWindow] = useState(7);

  const [docSearch, setDocSearch] = useState("");
  const [docCategoryFilter, setDocCategoryFilter] = useState("all");
  const [docMethodFilter, setDocMethodFilter] = useState("all");

  const [selectedDocId, setSelectedDocId] = useState(null);
  const [tryMode, setTryMode] = useState("live");
  const [tryMethod, setTryMethod] = useState("GET");
  const [tryPath, setTryPath] = useState("");
  const [tryApiKey, setTryApiKey] = useState("");
  const [tryBody, setTryBody] = useState("{}");
  const [tryResult, setTryResult] = useState(null);
  const [sendingTry, setSendingTry] = useState(false);
  const [syncingDocs, setSyncingDocs] = useState(false);

  const [profileSaving, setProfileSaving] = useState(false);
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [adminUsageLoading, setAdminUsageLoading] = useState(false);
  const [adminUsageSummary, setAdminUsageSummary] = useState([]);
  const [adminSelectedUser, setAdminSelectedUser] = useState(null);
  const [adminUserLogs, setAdminUserLogs] = useState([]);
  const [adminUsageView, setAdminUsageView] = useState("users");
  const [adminRoleUpdatingUserId, setAdminRoleUpdatingUserId] = useState(null);
  const [adminAccountUpdatingUserId, setAdminAccountUpdatingUserId] = useState(null);
  const [adminSelectedLog, setAdminSelectedLog] = useState(null);
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
  const [profileForm] = Form.useForm();
  const [passwordForm] = Form.useForm();
  const [headerMenuOpen, setHeaderMenuOpen] = useState(false);
  const [createUserModalOpen, setCreateUserModalOpen] = useState(false);
  const [createUserForm] = Form.useForm();
  const [creatingUser, setCreatingUser] = useState(false);

  const menuItems = useMemo(() => {
    const baseItems = [
      { key: "overview", icon: <AppstoreOutlined />, label: "Overview" },
      { key: "api-keys", icon: <KeyOutlined />, label: "API Keys" },
      { key: "usage", icon: <LineChartOutlined />, label: "Usage" },
      { key: "docs", icon: <FileTextOutlined />, label: "Documentation" },
    ];
    if (!isAdmin) return baseItems;
    return [...baseItems, { key: "admin-usage", icon: <BarChartOutlined />, label: "Admin Usage" }];
  }, [isAdmin]);

  const headerMenuItems = [
    {
      key: "profile-menu",
      label: "Profile",
      icon: <UserOutlined />,
      onClick: () => {
        setActiveKey("profile");
        setHeaderMenuOpen(false);
      },
    },
    {
      type: "divider",
    },
    {
      key: "logout",
      label: "Logout",
      icon: <LogoutOutlined />,
      onClick: () => {
        setHeaderMenuOpen(false);
        logout();
      },
      danger: true,
    },
  ];

  const refreshData = async () => {
    if (!currentUser?.id) return;
    setLoading(true);

    try {
      const [allRes, ownedRes, keyRes, logRes, profileRes, billingRes] = await Promise.allSettled([
        ApiServices.getAllEndpoints(),
        ApiServices.getUsersAllEndpoints(currentUser.id),
        ApiKeyServices.getUserApiKeys({ user_id: currentUser.id }),
        ApiLogService.apiLogService(),
        ProfileServices.getMyProfile(),
        axios.get("/api/v1/account/billing-config"),
      ]);

      const endpoints = allRes.status === "fulfilled" && Array.isArray(allRes.value?.data) ? allRes.value.data : [];
      const owned = ownedRes.status === "fulfilled" && Array.isArray(ownedRes.value?.data) ? ownedRes.value.data : [];
      const keys = keyRes.status === "fulfilled" && Array.isArray(keyRes.value?.data?.keys) ? keyRes.value.data.keys : [];
      const apiLogs = logRes.status === "fulfilled" && Array.isArray(logRes.value?.data) ? logRes.value.data : [];
      const profileData = profileRes.status === "fulfilled" ? profileRes.value?.data || null : null;
      const billingData = billingRes.status === "fulfilled" ? billingRes.value?.data || null : null;

      setAllEndpoints(endpoints);
      setOwnedEndpoints(owned);
      setGlobalKeys(keys);
      setLogs(apiLogs);
      setMyProfile(profileData);
      setBillingConfig(billingData);

      const failedCalls = [allRes, ownedRes, keyRes, logRes, profileRes, billingRes].filter((item) => item.status === "rejected");
      if (failedCalls.length > 0) {
        notification.warning({
          message: "Partial data loaded",
          description: "Some sections failed to load. Please re-login or refresh and try again.",
        });
      }

      if (!selectedDocId && endpoints.length > 0) {
        const first = endpoints[0];
        setSelectedDocId(first.id);
      }
    } catch (_error) {
      notification.error({
        message: "Load Failed",
        description: "Cannot load dashboard data.",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.id]);

  useEffect(() => {
    if (activeKey !== "profile" || !myProfile) return;
    profileForm.setFieldsValue({
      username: myProfile.username,
      email: myProfile.email,
    });
  }, [activeKey, myProfile, profileForm]);

  useEffect(() => {
    if (tryApiKey || !globalKeys.length) return;
    setTryApiKey(globalKeys[0]?.key || "");
  }, [globalKeys, tryApiKey]);

  const selectedDoc = useMemo(
    () => allEndpoints.find((item) => item.id === selectedDocId) || null,
    [allEndpoints, selectedDocId]
  );

  const usageSummary = useMemo(() => {
    const total = logs.length;
    const success = logs.filter((item) => item.status_code >= 200 && item.status_code < 300).length;
    const fail = logs.filter((item) => item.status_code >= 400).length;
    return {
      total,
      success,
      fail,
      successRate: total ? Math.round((success / total) * 100) : 0,
    };
  }, [logs]);

  const usageTable = useMemo(() => {
    return logs.slice().reverse().slice(0, 20).map((item, index) => ({
      key: `${item.id || index}`,
      method: item.method,
      endpoint: item.endpoint,
      status: item.status_code,
      time: moment(item.createdAt).format("YYYY-MM-DD HH:mm:ss"),
    }));
  }, [logs]);

  const topEndpoints = useMemo(() => {
    const countMap = logs.reduce((acc, item) => {
      const key = item.endpoint || "Unknown";
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});

    return Object.entries(countMap)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8)
      .map(([endpoint, count], index) => ({ key: `${index}-${endpoint}`, endpoint, count }));
  }, [logs]);

  const statusDistribution = useMemo(() => {
    const countMap = logs.reduce((acc, item) => {
      const key = String(item.status_code || "N/A");
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});

    return Object.entries(countMap)
      .sort((a, b) => Number(a[0]) - Number(b[0]))
      .map(([status, count], index) => ({ key: `${index}-${status}`, status, count }));
  }, [logs]);

  const usageSeries = useMemo(() => buildUsageSeries(logs, usageWindow), [logs, usageWindow]);

  const usageChartData = useMemo(() => {
    return {
      labels: usageSeries.labels,
      datasets: [
        {
          label: "Requests",
          data: usageSeries.requests,
          borderColor: "#00a4bc",
          backgroundColor: "rgba(0, 164, 188, 0.18)",
          tension: 0.35,
          fill: true,
        },
        {
          label: "Success",
          data: usageSeries.success,
          borderColor: "#4995ad",
          backgroundColor: "rgba(73, 149, 173, 0.2)",
          tension: 0.35,
          fill: true,
        },
        {
          label: "Errors",
          data: usageSeries.errors,
          borderColor: "#eb6363", // Coral
          backgroundColor: "rgba(235, 99, 99, 0.2)",
          tension: 0.35,
          fill: true,
        },
      ],
    };
  }, [usageSeries]);

  const usageChartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          labels: {
            color: "#016282",
          },
        },
      },
      scales: {
        x: {
          ticks: { color: "#00a4bc" },
          grid: { color: "rgba(1, 98, 130, 0.05)" },
        },
        y: {
          ticks: { color: "#00a4bc", precision: 0 },
          grid: { color: "rgba(1, 98, 130, 0.05)" },
        },
      },
    }),
    []
  );

  const endpointTable = useMemo(() => {
    const fallbackGlobalKey = globalKeys.find((item) => item?.status !== false)?.key || globalKeys[0]?.key || "";
    const sourceEndpoints = ownedEndpoints.length > 0 ? ownedEndpoints : allEndpoints;

    return sourceEndpoints.map((item, index) => {
      const endpointApiKey = item?.api_key || item?.apiKey || item?.key || "";
      const apiKey = endpointApiKey || fallbackGlobalKey || "-";
      const keySource = endpointApiKey ? "Endpoint" : fallbackGlobalKey ? "Global" : "None";

      return {
        key: `${item.id || index}`,
        name: item.name,
        category: item.category,
        method: item.method,
        apiKey,
        keySource,
        status: item.status,
      };
    });
  }, [ownedEndpoints, allEndpoints, globalKeys]);

  const productsEndpoints = useMemo(() => {
    return allEndpoints.filter((item) => String(item?.category || "").toLowerCase() === "products");
  }, [allEndpoints]);

  const globalKeyTable = useMemo(() => {
    return globalKeys.map((item, index) => ({
      key: item.id || `${index}`,
      value: item.key,
      createdAt: moment(item.createdAt).format("YYYY-MM-DD HH:mm:ss"),
    }));
  }, [globalKeys]);

  const docsSourceEndpoints = useMemo(() => {
    if (isAdmin) return allEndpoints;
    return allEndpoints.filter((item) => String(item.category || "").toLowerCase() === "products");
  }, [allEndpoints, isAdmin]);

  const docCategories = useMemo(() => {
    const categories = Array.from(new Set(docsSourceEndpoints.map((item) => item.category || "Others")));
    return categories.sort((a, b) => a.localeCompare(b));
  }, [docsSourceEndpoints]);

  const filteredDocs = useMemo(() => {
    const keyword = docSearch.trim().toLowerCase();
    return docsSourceEndpoints.filter((endpoint) => {
      if (docCategoryFilter !== "all" && (endpoint.category || "Others") !== docCategoryFilter) {
        return false;
      }
      if (docMethodFilter !== "all" && endpoint.method !== docMethodFilter) {
        return false;
      }
      if (!keyword) return true;

      const haystack = `${endpoint.name || ""} ${endpoint.url || ""} ${endpoint.description || ""}`.toLowerCase();
      return haystack.includes(keyword);
    });
  }, [docsSourceEndpoints, docSearch, docCategoryFilter, docMethodFilter]);

  const adminUsersWithLogs = useMemo(
    () => adminUsageSummary.filter((item) => Number(item.total_requests || 0) > 0).length,
    [adminUsageSummary]
  );

  const selectedAdminUserInfo = useMemo(
    () => adminUsageSummary.find((item) => item.user_id === adminSelectedUser) || null,
    [adminUsageSummary, adminSelectedUser]
  );

  const accountStatusLabel = useMemo(() => {
    const currentPlan = String(billingConfig?.currentPlan || "Free").toLowerCase();
    return currentPlan === "free" ? "Free" : "Premium";
  }, [billingConfig]);

  const groupedDocs = useMemo(() => {
    return filteredDocs.reduce((acc, endpoint) => {
      const key = endpoint.category || "Others";
      if (!acc[key]) acc[key] = [];
      acc[key].push(endpoint);
      return acc;
    }, {});
  }, [filteredDocs]);

  useEffect(() => {
    if (!selectedDocId && filteredDocs.length > 0) {
      setSelectedDocId(filteredDocs[0].id);
      return;
    }

    if (selectedDocId && !filteredDocs.some((item) => item.id === selectedDocId)) {
      setSelectedDocId(filteredDocs.length > 0 ? filteredDocs[0].id : null);
    }
  }, [filteredDocs, selectedDocId]);

  useEffect(() => {
    if (!selectedDoc) return;
    setTryMethod(selectedDoc.method || "GET");
    setTryPath(normalizePathForTry(selectedDoc.url || "/"));
    setTryBody(formatJson(getDocSampleBody(selectedDoc)));
    setTryResult(null);
  }, [selectedDoc]);

  const logout = () => {
    AuthService.logout();
    dispatch(setUser(null));
    dispatch(setAuth({ isAuthenticating: false, isAuthenticated: false }));
  };

  const loadAdminUsageSummary = async () => {
    if (!isAdmin) return;
    try {
      setAdminUsageLoading(true);
      const res = await axios.get("/api/v1/admin/usage-summary");
      const users = Array.isArray(res.data?.users) ? res.data.users : [];
      setAdminUsageSummary(users);

      const hasSelectedUser = users.some((item) => item.user_id === adminSelectedUser);
      if (!hasSelectedUser) {
        setAdminSelectedUser(null);
        setAdminUserLogs([]);
        setAdminUsageView("users");
      }
    } catch (error) {
      notification.error({
        message: "Load admin usage failed",
        description: error?.response?.data?.message || "Unable to load usage summary.",
      });
    } finally {
      setAdminUsageLoading(false);
    }
  };

  const loadAdminUserLogs = async (userId) => {
    if (!isAdmin || !userId) return;
    try {
      setAdminUsageLoading(true);
      const res = await axios.get(`/api/v1/admin/usage/${userId}?limit=100`);
      const logs = Array.isArray(res.data?.logs) ? res.data.logs : [];
      setAdminUserLogs(logs);
    } catch (error) {
      notification.error({
        message: "Load user logs failed",
        description: error?.response?.data?.message || "Unable to load user logs.",
      });
    } finally {
      setAdminUsageLoading(false);
    }
  };

  const openAdminUserHistory = (userId) => {
    if (!userId) return;
    setAdminSelectedUser(userId);
    setAdminSelectedLog(null);
    setAdminUsageView("logs");
  };

  const openAdminLogDetail = (record) => {
    if (!record) return;
    setAdminSelectedLog(record);
  };

  const changeAdminRole = async (userId, action) => {
    if (!userId || !["upgrade", "revoke"].includes(action)) return;

    try {
      setAdminRoleUpdatingUserId(String(userId));
      const res = await axios.post(`/api/v1/admin/role/${userId}/${action}`);

      notification.success({
        message: action === "upgrade" ? "Admin role upgraded" : "Admin role revoked",
        description: res?.data?.message || undefined,
      });

      await loadAdminUsageSummary();
    } catch (error) {
      notification.error({
        message: action === "upgrade" ? "Upgrade admin failed" : "Revoke admin failed",
        description: error?.response?.data?.message || "Please try again.",
      });
    } finally {
      setAdminRoleUpdatingUserId(null);
    }
  };

  const changeUserAccountStatus = async (userId, action) => {
    if (!userId || !["disable", "enable"].includes(action)) return;

    try {
      setAdminAccountUpdatingUserId(String(userId));
      const res = await axios.post(`/api/v1/admin/account/${userId}/${action}`);

      notification.success({
        message: action === "disable" ? "User account disabled" : "User account enabled",
        description: res?.data?.message || undefined,
      });

      await loadAdminUsageSummary();
    } catch (error) {
      notification.error({
        message: action === "disable" ? "Disable user failed" : "Enable user failed",
        description: error?.response?.data?.message || "Please try again.",
      });
    } finally {
      setAdminAccountUpdatingUserId(null);
    }
  };

  const confirmUserAccountToggle = (record) => {
    if (!record?.user_id) return;

    const isDisabled = record.is_disabled;
    const action = isDisabled ? "enable" : "disable";
    const title = isDisabled ? "Enable this account?" : "Disable this account?";
    const content = isDisabled
      ? "This user will be allowed to sign in again."
      : "All API keys for this user will be disabled immediately, and future requests will be blocked.";

    Modal.confirm({
      title,
      content,
      okText: isDisabled ? "Enable" : "Disable",
      okButtonProps: { danger: !isDisabled },
      cancelText: "Cancel",
      centered: true,
      onOk: () => changeUserAccountStatus(record.user_id, action),
    });
  };

  useEffect(() => {
    if (activeKey !== "admin-usage" || !isAdmin) return;
    loadAdminUsageSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeKey, isAdmin]);

  useEffect(() => {
    if (!adminSelectedUser || !isAdmin || activeKey !== "admin-usage" || adminUsageView !== "logs") return;
    loadAdminUserLogs(adminSelectedUser);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminSelectedUser, isAdmin, activeKey, adminUsageView]);

  const createGlobalKey = async () => {
    try {
      setLoading(true);
      await ApiKeyServices.generateApiKey(null, { user_id: currentUser.id });
      notification.success({ message: "API key generated." });
      await refreshData();
    } catch (error) {
      notification.error({ message: "Create key failed", description: error?.response?.data?.message || "Please try again." });
    } finally {
      setLoading(false);
    }
  };

  const deleteGlobalKey = async (value) => {
    try {
      setLoading(true);
      await ApiKeyServices.deleteApiKey({ api_key: value });
      notification.success({ message: "API key deleted." });
      await refreshData();
    } catch (error) {
      notification.error({ message: "Delete key failed", description: error?.response?.data?.message || "Please try again." });
    } finally {
      setLoading(false);
    }
  };

  const sendTryRequest = async () => {
    setSendingTry(true);
    setTryResult(null);

    try {
      const pathWithParams = applyPathParams(tryPath, tryBody);
      const unresolvedPlaceholders = (pathWithParams.match(/\{[^}]+\}/g) || []).length;
      const path = normalizePathForTry(pathWithParams);
      const normalizedApiKey = (tryApiKey || "").replace(/\s+/g, "").trim();

      if (unresolvedPlaceholders > 0) {
        setTryResult({
          ok: false,
          status: 400,
          data: {
            message: "Please fill path placeholders in Body JSON before sending.",
            hint: "Add values for txid/productId, then click Send again.",
          },
        });
        setSendingTry(false);
        return;
      }

      if (tryMode === "live" && !normalizedApiKey) {
        setTryResult({
          ok: false,
          status: 400,
          data: { message: "API key is required in Live mode." },
        });
        setSendingTry(false);
        return;
      }

      if (tryMode === "mock") {
        const mockData = {
          mode: "mock",
          method: tryMethod,
          path,
          endpoint: selectedDoc?.name,
          sample: {
            headers: safeParse(selectedDoc?.headers, {}),
            params: safeParse(selectedDoc?.params, {}),
            query: safeParse(selectedDoc?.query, {}),
            body: getDocSampleBody(selectedDoc),
          },
        };
        setTryResult({ ok: true, status: 200, data: mockData });
        setSendingTry(false);
        return;
      }

      const requestConfig = {
        method: tryMethod || "GET",
        url: path,
        headers: {
          Authorization: `Bearer ${normalizedApiKey}`,
          "Content-Type": "application/json",
        },
      };

      if (!["GET", "DELETE"].includes((tryMethod || "GET").toUpperCase())) {
        try {
          requestConfig.data = tryBody ? JSON.parse(tryBody) : {};
        } catch (_error) {
          setTryResult({
            ok: false,
            status: 400,
            data: { message: "Body must be valid JSON." },
          });
          setSendingTry(false);
          return;
        }
      }

      const response = await axios(requestConfig);
      setTryResult({ ok: true, status: response.status, data: response.data });
    } catch (error) {
      const isNetworkError = !error?.response;
      const statusCode = error?.response?.status || 500;
      const responseData = error?.response?.data;
      const isHtmlPayload = typeof responseData === "string" && /<html[\s>]/i.test(responseData);

      let normalizedErrorData = responseData;
      if (statusCode === 405) {
        normalizedErrorData = {
          message: "Upstream returned 405 Method Not Allowed.",
          hint: "Your API key passed local validation. This is now an upstream path/method contract issue, not a JSON type issue.",
          upstream: normalizePathForTry(tryPath),
          detail: isHtmlPayload ? "Nginx HTML error page was returned by upstream." : responseData,
        };
      }

      setTryResult({
        ok: false,
        status: statusCode,
        data: normalizedErrorData || {
          message: isNetworkError ? "Network Error" : error.message,
          hint: isNetworkError
            ? "Please check server MAIN_SERVER_URL and backend proxy target."
            : undefined,
        },
      });
    } finally {
      setSendingTry(false);
    }
  };

  const syncOnlineEndpointTemplates = async () => {
    try {
      setSyncingDocs(true);
      await ApiServices.seedAllApi();
      notification.success({ message: "Endpoint templates synced successfully." });
      await refreshData();
    } catch (error) {
      notification.error({
        message: "Sync failed",
        description: error?.response?.data?.message || "Unable to sync endpoint templates.",
      });
    } finally {
      setSyncingDocs(false);
    }
  };

  const tryPathNormalized = normalizePathForTry(applyPathParams(tryPath || selectedDoc?.url || "/", tryBody));
  const curlSnippet = [
    `curl -X ${tryMethod} "http://localhost:8081${tryPathNormalized}"`,
    `  -H "Authorization: Bearer ${tryApiKey || "<API_KEY>"}"`,
    `  -H "Content-Type: application/json"`,
    !["GET", "DELETE"].includes((tryMethod || "GET").toUpperCase())
      ? `  -d '${tryBody || "{}"}'`
      : null,
  ]
    .filter(Boolean)
    .join(" \\\n+");

  const axiosSnippet = `axios({\n  method: "${tryMethod}",\n  url: "${tryPathNormalized}",\n  headers: {\n    Authorization: "Bearer ${tryApiKey || "<API_KEY>"}",\n    "Content-Type": "application/json"\n  },\n  data: ${["GET", "DELETE"].includes((tryMethod || "GET").toUpperCase()) ? "undefined" : tryBody || "{}"}\n});`;

  const copyText = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      notification.success({ message: `${label} copied` });
    } catch (error) {
      notification.error({ message: `Failed to copy ${label}` });
    }
  };

  const showUpgradeInfo = () => {
    setUpgradeModalOpen(true);
  };

  const openEndpointInDocs = (endpointId) => {
    if (!endpointId) return;
    setSelectedDocId(endpointId);
    setActiveKey("docs");
  };

  const renderApiKeyValue = (value) => {
    if (!value || value === "-") return <Text type="secondary">-</Text>;

    return (
      <div className="api-key-cell">
        <Text className="api-key-value">{value}</Text>
        <Button
          size="small"
          icon={<CopyOutlined />}
          className="copy-key-btn"
          onClick={() => copyText(value, "API key")}
        >
          Copy
        </Button>
      </div>
    );
  };

  const handleProfileSave = async () => {
    try {
      const values = await profileForm.validateFields();
      setProfileSaving(true);

      const res = await ProfileServices.updateMyProfile({
        username: values.username,
        email: myProfile?.email || values.email,
      });
      const updatedUser = res.data?.user;
      if (updatedUser) {
        const old = AuthService.getCurrentUser() || {};
        const merged = { ...old, username: updatedUser.username, email: updatedUser.email };
        localStorage.setItem("user", JSON.stringify(merged));
        dispatch(setUser(merged));
        setMyProfile((prev) => ({ ...prev, ...updatedUser }));
      }

      notification.success({ message: "Profile updated successfully." });
    } catch (error) {
      notification.error({
        message: "Update profile failed",
        description: error?.response?.data?.message || error?.message || "Please try again.",
      });
    } finally {
      setProfileSaving(false);
    }
  };

  const handlePasswordSave = async () => {
    try {
      const values = await passwordForm.validateFields();
      if (values.newPassword !== values.confirmPassword) {
        notification.error({ message: "New password and confirm password do not match." });
        return;
      }

      setPasswordSaving(true);
      await ProfileServices.changeMyPassword({
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
      });
      passwordForm.resetFields();
      notification.success({ message: "Password updated successfully." });
    } catch (error) {
      notification.error({
        message: "Update password failed",
        description: error?.response?.data?.message || error?.message || "Please try again.",
      });
    } finally {
      setPasswordSaving(false);
    }
  };

  const renderOverview = () => (
    <>
      <Title level={2} style={{ color: "#174a60", marginTop: 0 }}>Welcome back, {currentUser?.username || "User"}!</Title>
      <Text style={{ color: "#45667a" }}>Manage your API keys and monitor usage.</Text>

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} md={8}>
          <Card className="portal-card" loading={loading}><Statistic title="Active Keys" value={globalKeys.length} /></Card>
        </Col>
        <Col xs={24} md={8}>
          <Card className="portal-card" loading={loading}>
            <Statistic
              title={isAdmin ? "Registered Endpoints" : "Products Endpoints"}
              value={isAdmin ? allEndpoints.length : productsEndpoints.length}
            />
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card className="portal-card" loading={loading}><Statistic title="Requests Logged" value={usageSummary.total} /></Card>
        </Col>
      </Row>

      <Card className="portal-card api-overview-card" style={{ marginTop: 16 }}>
        <div className="api-overview-item">
          <div>
            <Text strong>Step 1: Create Access Key</Text>
            <div className="api-overview-desc">
              Create one global API key for your account. Use Bearer &lt;API_KEY&gt; in Try Endpoint live mode.
            </div>
          </div>
          <Space>
            <Tag color={globalKeys.length > 0 ? "success" : "default"}>{globalKeys.length > 0 ? "Done" : "Pending"}</Tag>
            <Button onClick={() => setActiveKey("api-keys")}>{globalKeys.length > 0 ? "Manage Keys" : "Create Key"}</Button>
          </Space>
        </div>
        <div className="api-overview-item">
          <div>
            <Text strong>Step 2: Select API in Documentation</Text>
            <div className="api-overview-desc">
              Choose endpoint, review request schema, then test with Try Endpoint before integration.
            </div>
          </div>
          <Button onClick={() => setActiveKey("docs")}>Open Docs</Button>
        </div>

        <div className="api-overview-item">
          <div>
            <Text strong>Step 3: Send First Request + Copy Snippets</Text>
            <div className="api-overview-desc">
              Use Try Endpoint in Live mode. When successful, copy cURL/Axios snippets into your app.
            </div>
          </div>
          <Button onClick={() => setActiveKey("docs")}>Try Endpoint</Button>
        </div>

        <div className="api-overview-item">
          <div>
            <Text strong>Step 4: Monitor Health</Text>
            <div className="api-overview-desc">
              Track request count, success rate, status distribution, and top endpoints in Usage Analytics.
            </div>
          </div>
          <Button onClick={() => setActiveKey("usage")}>Open Usage</Button>
        </div>
      </Card>

      <Card className="portal-card" title="Integration Tips" style={{ marginTop: 16 }}>
        <Space direction="vertical" size={6} style={{ width: "100%" }}>
          <Text>1) Use the same API key in your server-side integration.</Text>
          <Text>2) Always test with one known-good sample body first.</Text>
          <Text>3) If you get 4xx/5xx, check Usage logs to confirm path, method, and status.</Text>
        </Space>
      </Card>

      <Card className="portal-card" title="Products Endpoints" style={{ marginTop: 16 }}>
        <Text style={{ color: "#4f6f7f", display: "block", marginBottom: 12 }}>
          {isAdmin
            ? "Products endpoints are the client-facing priority. Keep these templates accurate."
            : "Start from PRODUCTS endpoints first. This is the fastest path to your first successful call."}
        </Text>
        {productsEndpoints.length === 0 ? (
          <Empty description="No products endpoints found" />
        ) : (
          <Table
            size="small"
            pagination={false}
            dataSource={productsEndpoints.slice(0, 6).map((item, index) => ({
              key: `${item.id || index}`,
              id: item.id,
              name: item.name,
              method: item.method,
              description: item.description,
            }))}
            columns={[
              { title: "Endpoint", dataIndex: "name", key: "name" },
              {
                title: "Method",
                dataIndex: "method",
                key: "method",
                width: 110,
                render: (value) => <Tag color={value === "GET" ? "green" : "blue"}>{value}</Tag>,
              },
              { title: "Description", dataIndex: "description", key: "description", ellipsis: true },
              {
                title: "Action",
                key: "action",
                width: 130,
                render: (_, record) => (
                  <Button size="small" onClick={() => openEndpointInDocs(record.id)}>Open in Docs</Button>
                ),
              },
            ]}
          />
        )}
      </Card>
    </>
  );

  const renderApiKeys = () => (
    <>
      <Space align="center" style={{ justifyContent: "space-between", width: "100%" }}>
        <div>
          <Title level={2} style={{ color: "#174a60", marginTop: 0 }}>API Keys</Title>
          <Text style={{ color: "#45667a" }}>Generate and manage global API keys.</Text>
        </div>
        <Button type="primary" onClick={createGlobalKey}>Create New Key</Button>
      </Space>

      <Card className="portal-card" style={{ marginTop: 16 }}>
        {globalKeyTable.length === 0 ? (
          <Empty description="No API keys yet" />
        ) : (
          <Table
            dataSource={globalKeyTable}
            pagination={{ pageSize: 6 }}
            scroll={{ x: 860 }}
            columns={[
              { title: "API Key", dataIndex: "value", key: "value", render: (value) => renderApiKeyValue(value) },
              { title: "Created At", dataIndex: "createdAt", key: "createdAt" },
              { title: "Action", key: "action", render: (_, record) => <Button danger onClick={() => deleteGlobalKey(record.value)}>Delete</Button> },
            ]}
          />
        )}
      </Card>

      <Card className="portal-card" title="Endpoint Access Mapping" style={{ marginTop: 16 }}>
        <Table
          pagination={{ pageSize: 6 }}
          dataSource={endpointTable}
          scroll={{ x: 960 }}
          columns={[
            { title: "Name", dataIndex: "name", key: "name" },
            { title: "Category", dataIndex: "category", key: "category" },
            {
              title: "Method",
              dataIndex: "method",
              key: "method",
              width: 90,
              render: (value) => <Tag color={value === "GET" ? "green" : "blue"}>{value}</Tag>,
            },
            { title: "API Key", dataIndex: "apiKey", key: "apiKey", render: (value) => renderApiKeyValue(value) },
            { title: "Key Source", dataIndex: "keySource", key: "keySource", width: 120, render: (value) => <Tag>{value}</Tag> },
            {
              title: "Status",
              dataIndex: "status",
              key: "status",
              width: 110,
              render: (value) => <Tag color={value ? "success" : "default"}>{value ? "Active" : "Inactive"}</Tag>,
            },
          ]}
        />
      </Card>
    </>
  );

  const renderUsage = () => (
    <>
      <Space align="center" style={{ justifyContent: "space-between", width: "100%" }}>
        <div>
          <Title level={2} style={{ color: "#174a60", marginTop: 0 }}>Usage Analytics</Title>
          <Text style={{ color: "#45667a" }}>Recent traffic and status summary from API logs.</Text>
        </div>
        <Space>
          <Button type={usageWindow === 7 ? "primary" : "default"} onClick={() => setUsageWindow(7)}>7 Days</Button>
          <Button type={usageWindow === 30 ? "primary" : "default"} onClick={() => setUsageWindow(30)}>30 Days</Button>
          <Button type={usageWindow === 90 ? "primary" : "default"} onClick={() => setUsageWindow(90)}>90 Days</Button>
        </Space>
      </Space>

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} md={6}><Card className="portal-card"><Statistic title="Total" value={usageSummary.total} /></Card></Col>
        <Col xs={24} md={6}><Card className="portal-card"><Statistic title="Success" value={usageSummary.success} /></Card></Col>
        <Col xs={24} md={6}><Card className="portal-card"><Statistic title="Errors" value={usageSummary.fail} /></Card></Col>
        <Col xs={24} md={6}><Card className="portal-card"><Statistic title="Success Rate" value={`${usageSummary.successRate}%`} /></Card></Col>
      </Row>

      <Card className="portal-card" style={{ marginTop: 16 }}>
        <div style={{ height: 320 }}>
          <Line options={usageChartOptions} data={usageChartData} />
        </div>
      </Card>

      <Card className="portal-card" style={{ marginTop: 16 }}>
        <Table
          dataSource={usageTable}
          pagination={{ pageSize: 8 }}
          columns={[
            { title: "Method", dataIndex: "method", key: "method", render: (value) => <Tag color={value === "GET" ? "green" : "blue"}>{value}</Tag> },
            { title: "Endpoint", dataIndex: "endpoint", key: "endpoint", ellipsis: true },
            { title: "Status", dataIndex: "status", key: "status", render: (value) => <Tag color={value >= 400 ? "red" : "green"}>{value}</Tag> },
            { title: "Time", dataIndex: "time", key: "time" },
          ]}
        />
      </Card>

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} lg={12} className="usage-bottom-col">
          <Card className="portal-card usage-equal-card" title="Top Endpoints">
            <Table
              dataSource={topEndpoints}
              pagination={false}
              size="small"
              scroll={{ y: 252 }}
              columns={[
                { title: "Endpoint", dataIndex: "endpoint", key: "endpoint", ellipsis: true },
                { title: "Hits", dataIndex: "count", key: "count", width: 100 },
              ]}
            />
          </Card>
        </Col>
        <Col xs={24} lg={12} className="usage-bottom-col">
          <Card className="portal-card usage-equal-card" title="Status Distribution">
            <Table
              dataSource={statusDistribution}
              pagination={false}
              size="small"
              scroll={{ y: 252 }}
              columns={[
                {
                  title: "Status",
                  dataIndex: "status",
                  key: "status",
                  width: 120,
                  render: (value) => <Tag color={Number(value) >= 400 ? "red" : "green"}>{value}</Tag>,
                },
                { title: "Count", dataIndex: "count", key: "count", width: 100 },
              ]}
            />
          </Card>
        </Col>
      </Row>
    </>
  );

  const renderBilling = () => (
    <>
      <Title level={2} style={{ color: "#174a60", marginTop: 0 }}>Subscription & Billing</Title>
      <Text style={{ color: "#45667a" }}>Plan cards are now loaded from backend config.</Text>
      <Card className="portal-card" style={{ marginTop: 16 }}>
        <Text style={{ display: "block", marginBottom: 12, color: "#2d5166" }}>
          Current Plan: <b>{billingConfig?.currentPlan || "Free"}</b> | Usage this month: <b>{billingConfig?.monthlyUsed ?? 0}</b>
        </Text>
        <Row gutter={[16, 16]}>
          {(billingConfig?.plans || [
            { key: "free", name: "Free", priceMonthly: 0, requestsPerMinute: 10, monthlyLimit: 10000 },
            { key: "pro", name: "Pro", priceMonthly: 49, requestsPerMinute: 100, monthlyLimit: 1000000 },
            { key: "enterprise", name: "Enterprise", priceMonthly: 299, requestsPerMinute: -1, monthlyLimit: -1 },
          ]).map((plan) => (
            <Col xs={24} md={8} key={plan.key}>
              <Card className="plan-card">
                <Title level={3}>{plan.name}</Title>
                <Text>${plan.priceMonthly}/month</Text>
                <br />
                <Text>{plan.requestsPerMinute < 0 ? "Unlimited" : `${plan.requestsPerMinute} req/min`}</Text>
                <br />
                <Text>{plan.monthlyLimit < 0 ? "Unlimited monthly" : `${plan.monthlyLimit.toLocaleString()} req/month`}</Text>
              </Card>
            </Col>
          ))}
        </Row>
      </Card>
    </>
  );

  const renderProfile = () => (
    <>
      <Title level={2} style={{ color: "#174a60", marginTop: 0 }}>Profile</Title>
      <Text style={{ color: "#45667a" }}>Update your account details and password.</Text>

      <Row gutter={[16, 16]} style={{ marginTop: 16 }} className="profile-main-row">
        <Col xs={24} lg={12} className="profile-main-col">
          <Card className="portal-card profile-equal-card" title="Edit Profile">
            <Form form={profileForm} layout="vertical">
              <Form.Item label="Username" name="username" rules={[{ required: true, message: "Please input username" }]}>
                <Input />
              </Form.Item>
              <Form.Item label="Email" name="email" rules={[{ required: true, message: "Please input email" }, { type: "email", message: "Email format is invalid" }]}>
                <Input disabled className="readonly-input" />
              </Form.Item>
              <Button type="primary" onClick={handleProfileSave} loading={profileSaving}>Save Profile</Button>
            </Form>

            <div className="profile-account-status-fill">
              <Space align="center" style={{ width: "100%", justifyContent: "space-between" }} wrap>
                <Space size={10}>
                  <Text strong>Account Status</Text>
                  <Tag color={accountStatusLabel === "Premium" ? "success" : "default"}>{accountStatusLabel}</Tag>
                </Space>
                <Button type="primary" onClick={showUpgradeInfo}>Upgrade to Pro</Button>
              </Space>
            </div>
          </Card>
        </Col>
        <Col xs={24} lg={12} className="profile-main-col">
          <Card className="portal-card profile-equal-card" title="Change Password">
            <Form form={passwordForm} layout="vertical">
              <Form.Item label="Current Password" name="currentPassword" rules={[{ required: true, message: "Please input current password" }]}>
                <Input.Password />
              </Form.Item>
              <Form.Item label="New Password" name="newPassword" rules={[{ required: true, message: "Please input new password" }, { min: 6, message: "At least 6 characters" }]}>
                <Input.Password />
              </Form.Item>
              <Form.Item label="Confirm New Password" name="confirmPassword" rules={[{ required: true, message: "Please confirm new password" }]}>
                <Input.Password />
              </Form.Item>
              <Button type="primary" onClick={handlePasswordSave} loading={passwordSaving}>Update Password</Button>
            </Form>
          </Card>
        </Col>
      </Row>

      <Card className="portal-card" style={{ marginTop: 16 }}>
        <Row gutter={[16, 16]}>
          <Col xs={24} md={12}><Text strong>Current Username</Text><div>{myProfile?.username || "-"}</div></Col>
          <Col xs={24} md={12}><Text strong>Current Email</Text><div>{myProfile?.email || "-"}</div></Col>
          <Col xs={24} md={12}><Text strong>Member Since</Text><div>{myProfile?.createdAt ? moment(myProfile.createdAt).format("YYYY-MM-DD") : "-"}</div></Col>
          <Col xs={24} md={12}><Text strong>Role(s)</Text><div>{(currentUser?.roles || []).join(", ")}</div></Col>
        </Row>
      </Card>
    </>
  );

  const renderDocs = () => {
    const sample = {
      headers: safeParse(selectedDoc?.headers, {}),
      params: safeParse(selectedDoc?.params, {}),
      query: safeParse(selectedDoc?.query, {}),
      body: getDocSampleBody(selectedDoc),
    };

    return (
      <>
        <Title level={2} style={{ color: "#174a60", marginTop: 0 }}>{isAdmin ? "API Documentation" : "Products API Documentation"}</Title>
        <Text style={{ color: "#45667a" }}>
          {isAdmin
            ? "Browse endpoints from DB, filter quickly, and run live/mock requests."
            : "Products endpoints are prioritized for clients. Pick one endpoint, run Try Endpoint, then copy snippets."}
        </Text>

        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          <Col xs={24}>
            <Card className="portal-card">
              <Row gutter={[12, 12]}>
                {isAdmin && (
                  <Col xs={24} md={8}>
                    <Button onClick={syncOnlineEndpointTemplates} loading={syncingDocs} block>
                      Sync Online Endpoint Templates
                    </Button>
                  </Col>
                )}
                <Col xs={24} md={8}>
                  <Input placeholder="Search endpoint name/url/description" value={docSearch} onChange={(e) => setDocSearch(e.target.value)} />
                </Col>
                <Col xs={12} md={4}>
                  <Select
                    value={docCategoryFilter}
                    onChange={setDocCategoryFilter}
                    style={{ width: "100%" }}
                    options={[{ label: "All Categories", value: "all" }, ...docCategories.map((c) => ({ label: c, value: c }))]}
                  />
                </Col>
                <Col xs={12} md={4}>
                  <Select
                    value={docMethodFilter}
                    onChange={setDocMethodFilter}
                    style={{ width: "100%" }}
                    options={[
                      { label: "All Methods", value: "all" },
                      { label: "GET", value: "GET" },
                      { label: "POST", value: "POST" },
                      { label: "PUT", value: "PUT" },
                      { label: "DELETE", value: "DELETE" },
                      { label: "PATCH", value: "PATCH" },
                    ]}
                  />
                </Col>
              </Row>
            </Card>
          </Col>

          <Col xs={24} lg={8}>
            <Card className="portal-card docs-list-card" title="Endpoints by Category">
              {Object.keys(groupedDocs).length === 0 && <Empty description="No endpoints" />}
              {Object.entries(groupedDocs).map(([category, endpoints]) => (
                <div key={category} style={{ marginBottom: 14 }}>
                  <Text className="docs-category">{category}</Text>
                  <div className="docs-list-wrap">
                    {endpoints.map((endpoint) => (
                      <button
                        type="button"
                        key={endpoint.id}
                        onClick={() => setSelectedDocId(endpoint.id)}
                        className={`docs-item-btn ${selectedDocId === endpoint.id ? "active" : ""}`}
                      >
                        <span>{endpoint.name}</span>
                        <Tag>{endpoint.method}</Tag>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </Card>
          </Col>

          <Col xs={24} lg={16}>
            <Card className="portal-card" title={selectedDoc ? `${selectedDoc.method}: ${selectedDoc.name}` : "Select Endpoint"}>
              {selectedDoc ? (
                <Space direction="vertical" size={12} style={{ width: "100%" }}>
                  <Text><b>Category:</b> {selectedDoc.category}</Text>
                  <Text><b>Path:</b> {normalizePathForTry(selectedDoc.url)}</Text>
                  <Text><b>Description:</b> {selectedDoc.description || "No description"}</Text>
                  <Text><b>Version:</b> {selectedDoc.version || "N/A"}</Text>

                  <Row gutter={[12, 12]}>
                    <Col xs={24} md={12}>
                      <Card className="try-card" title="Headers Schema / Sample"><pre>{formatJson(sample.headers)}</pre></Card>
                    </Col>
                    <Col xs={24} md={12}>
                      <Card className="try-card" title="Params Schema / Sample"><pre>{formatJson(sample.params)}</pre></Card>
                    </Col>
                    <Col xs={24} md={12}>
                      <Card className="try-card" title="Query Schema / Sample"><pre>{formatJson(sample.query)}</pre></Card>
                    </Col>
                    <Col xs={24} md={12}>
                      <Card className="try-card" title="Body Schema / Sample"><pre>{formatJson(sample.body)}</pre></Card>
                    </Col>
                  </Row>

                  <Card className="try-card" title="Try Endpoint">
                    <Row gutter={[12, 12]}>
                      <Col xs={24} md={6}>
                        <Text>Mode</Text>
                        <Select
                          style={{ width: "100%", marginTop: 4 }}
                          value={tryMode}
                          onChange={setTryMode}
                          options={[{ label: "Live", value: "live" }, { label: "Mock", value: "mock" }]}
                        />
                      </Col>
                      <Col xs={24} md={6}>
                        <Text>Method</Text>
                        <Select
                          style={{ width: "100%", marginTop: 4 }}
                          value={tryMethod}
                          onChange={setTryMethod}
                          options={["GET", "POST", "PUT", "DELETE", "PATCH"].map((item) => ({ label: item, value: item }))}
                        />
                      </Col>
                      <Col xs={24} md={12}>
                        <Text>Path</Text>
                        <Input value={tryPath} onChange={(e) => setTryPath(e.target.value)} style={{ marginTop: 4 }} />
                      </Col>
                      <Col xs={24}>
                        <Text>API Key (required in Live mode)</Text>
                        <Input.Password value={tryApiKey} onChange={(e) => setTryApiKey(e.target.value)} style={{ marginTop: 4 }} />
                      </Col>
                      <Col xs={24}>
                        <Text>Body (JSON)</Text>
                        <Input.TextArea rows={8} value={tryBody} onChange={(e) => setTryBody(e.target.value)} style={{ marginTop: 4 }} />
                      </Col>
                      <Col xs={24}>
                        <Button type="primary" onClick={sendTryRequest} loading={sendingTry}>Send</Button>
                      </Col>
                    </Row>
                  </Card>

                  <Card className="try-card result-card" title={tryResult ? `Result (${tryResult.status})` : "Result"}>
                    <pre>{tryResult ? JSON.stringify(tryResult.data, null, 2) : "No response yet. Click Send to test this endpoint."}</pre>
                  </Card>

                  <Card className="try-card" title="Code Snippets">
                    <Space direction="vertical" style={{ width: "100%" }}>
                      <div>
                        <Text strong>cURL</Text>
                        <pre>{curlSnippet}</pre>
                        <Button onClick={() => copyText(curlSnippet, "cURL snippet")}>Copy cURL</Button>
                      </div>
                      <div>
                        <Text strong>Axios</Text>
                        <pre>{axiosSnippet}</pre>
                        <Button onClick={() => copyText(axiosSnippet, "Axios snippet")}>Copy Axios</Button>
                      </div>
                    </Space>
                  </Card>
                </Space>
              ) : (
                <Empty description="No endpoint selected" />
              )}
            </Card>
          </Col>
        </Row>
      </>
    );
  };

  const renderAdminUsage = () => {
    const isLogsView = adminUsageView === "logs" && Boolean(adminSelectedUser);

    return (
      <>
        <Space align="center" style={{ justifyContent: "space-between", width: "100%" }}>
          <div>
            <Title level={2} style={{ color: "#174a60", marginTop: 0 }}>Admin Usage</Title>
            <Text style={{ color: "#45667a" }}>Monitor per-user API usage without changing existing database records.</Text>
          </div>
          <Space>
            <Button onClick={loadAdminUsageSummary} loading={adminUsageLoading}>Refresh</Button>
            {isAdmin && (
              <Button type="primary" onClick={() => { createUserForm.resetFields(); setCreateUserModalOpen(true); }}>
                Create User
              </Button>
            )}
          </Space>
        </Space>

        <Modal
          title="Create User"
          open={createUserModalOpen}
          onCancel={() => setCreateUserModalOpen(false)}
          centered
          okText="Create"
          confirmLoading={creatingUser}
          onOk={async () => {
            try {
              const values = await createUserForm.validateFields();
              setCreatingUser(true);
              const res = await axios.post('/api/v1/admin/account/create', values);
              notification.success({ message: 'User created', description: res?.data?.message || '' });
              setCreateUserModalOpen(false);
              await loadAdminUsageSummary();
            } catch (error) {
              const msg = error?.response?.data?.message || error?.message || 'Failed to create user.';
              notification.error({ message: 'Create user failed', description: msg });
            } finally {
              setCreatingUser(false);
            }
          }}
        >
          <Form form={createUserForm} layout="vertical">
            <Form.Item name="username" label="Username" rules={[{ required: true, message: 'Please input username' }]}>
              <Input />
            </Form.Item>
            <Form.Item name="email" label="Email" rules={[{ required: true, message: 'Please input email' }, { type: 'email', message: 'Invalid email' }]}>
              <Input />
            </Form.Item>
            <Form.Item name="password" label="Password" rules={[{ required: true, message: 'Please input password' }, { min: 6, message: 'Password must be at least 6 characters' }]}>
              <Input.Password />
            </Form.Item>
          </Form>
        </Modal>

        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          <Col xs={24} md={8}><Card className="portal-card"><Statistic title="Users with Logs" value={adminUsersWithLogs} /></Card></Col>
          <Col xs={24} md={8}><Card className="portal-card"><Statistic title="Total Requests" value={adminUsageSummary.reduce((acc, item) => acc + Number(item.total_requests || 0), 0)} /></Card></Col>
          <Col xs={24} md={8}><Card className="portal-card"><Statistic title="Selected User Logs" value={adminUserLogs.length} /></Card></Col>
        </Row>

        <Card
          className="portal-card admin-usage-single-card"
          style={{ marginTop: 16 }}
          title={isLogsView ? `Latest Logs${selectedAdminUserInfo ? ` for ${selectedAdminUserInfo.username || selectedAdminUserInfo.email || selectedAdminUserInfo.user_id}` : ""}` : "Usage By User"}
        >
          {isLogsView ? (
            <>
              <Table
                loading={adminUsageLoading}
                dataSource={adminUserLogs.map((item, index) => ({
                  ...item,
                  key: item.id || `${index}`,
                  time: item.createdAt ? moment(item.createdAt).format("YYYY-MM-DD HH:mm:ss") : "-",
                }))}
                pagination={{ pageSize: 10 }}
                tableLayout="fixed"
                scroll={{ x: 760 }}
                rowClassName={() => "admin-log-row-clickable"}
                onRow={(record) => ({
                  onClick: () => openAdminLogDetail(record),
                  style: { cursor: "pointer" },
                })}
                columns={[
                  { title: "Method", dataIndex: "method", key: "method", width: 90 },
                  { title: "Endpoint", dataIndex: "endpoint", key: "endpoint", ellipsis: true },
                  { title: "Status", dataIndex: "status", key: "status", width: 90, render: (value) => <Tag color={value >= 400 ? "red" : "green"}>{value}</Tag> },
                  { title: "Time", dataIndex: "time", key: "time", width: 180 },
                  {
                    title: "Detail",
                    key: "detail",
                    width: 120,
                    render: (_, record) => (
                      <Button
                        size="small"
                        onClick={(event) => {
                          event.stopPropagation();
                          openAdminLogDetail(record);
                        }}
                      >
                        View
                      </Button>
                    ),
                  },
                ]}
              />
              <div className="admin-usage-back-wrap">
                <Button onClick={() => setAdminUsageView("users")}>Back to User List</Button>
              </div>
            </>
          ) : (
            <Table
              loading={adminUsageLoading}
              dataSource={adminUsageSummary.map((item) => ({ ...item, key: item.user_id }))}
              pagination={{ pageSize: 8 }}
              tableLayout="fixed"
              scroll={{ x: 980 }}
              rowClassName={(record) => (record.user_id === adminSelectedUser ? "admin-row-selected" : "")}
              onRow={(record) => ({
                onClick: () => openAdminUserHistory(record.user_id),
                style: { cursor: "pointer" },
              })}
              columns={[
                {
                  title: "User",
                  dataIndex: "username",
                  key: "username",
                  width: 360,
                  render: (value) => (
                    <Text ellipsis={{ tooltip: value || "-" }} style={{ display: "inline-block", maxWidth: 320 }}>
                      {value || "-"}
                    </Text>
                  ),
                },
                {
                  title: "Email",
                  dataIndex: "email",
                  key: "email",
                  width: 320,
                  ellipsis: true,
                  render: (value) => (
                    <Text ellipsis={{ tooltip: value || "-" }} style={{ display: "inline-block", maxWidth: 280 }}>
                      {value || "-"}
                    </Text>
                  ),
                },
                { title: "Requests", dataIndex: "total_requests", key: "total_requests", width: 110 },
                { title: "Success %", dataIndex: "success_rate", key: "success_rate", width: 110, render: (v) => `${v}%` },
                {
                  title: "Role",
                  dataIndex: "is_admin",
                  key: "is_admin",
                  align: "center",
                  width: 170,
                  render: (_, record) => (
                    <div className="admin-role-inline">
                      <Tag color={record.is_disabled ? "error" : record.is_admin ? "processing" : "default"}>
                        {record.is_disabled ? "Disabled" : record.is_admin ? "Admin" : "User"}
                      </Tag>
                      <Button
                        size="small"
                        icon={<StopOutlined />}
                        danger={!record.is_disabled}
                        type={record.is_disabled ? "default" : "primary"}
                        disabled={String(record.user_id) === String(currentUser?.id)}
                        loading={String(adminAccountUpdatingUserId) === String(record.user_id)}
                        onClick={(event) => {
                          event.stopPropagation();
                          confirmUserAccountToggle(record);
                        }}
                        title={record.is_disabled ? "Enable Account" : "Disable Account"}
                        aria-label={record.is_disabled ? "Enable Account" : "Disable Account"}
                      />
                    </div>
                  ),
                },
                {
                  title: "Action",
                  key: "action",
                  align: "center",
                  width: 150,
                  render: (_, record) => {
                    const isSelfAdmin = String(record.user_id) === String(currentUser?.id) && record.is_admin;
                    const nextAction = record.is_admin ? "revoke" : "upgrade";
                    const buttonLabel = record.is_admin ? "Revoke Admin" : "Upgrade to Admin";

                    return (
                      <div className="admin-action-cell">
                        <Button
                          size="small"
                          type={record.is_admin ? "default" : "primary"}
                          danger={record.is_admin}
                          disabled={isSelfAdmin}
                          loading={String(adminRoleUpdatingUserId) === String(record.user_id)}
                          onClick={(event) => {
                            event.stopPropagation();
                            changeAdminRole(record.user_id, nextAction);
                          }}
                        >
                          {buttonLabel}
                        </Button>
                      </div>
                    );
                  },
                },
              ]}
            />
          )}
        </Card>

        <Modal
          title="API Log Detail"
          open={Boolean(adminSelectedLog)}
          onCancel={() => setAdminSelectedLog(null)}
          footer={null}
          width={860}
          destroyOnClose
        >
          {adminSelectedLog ? (
            <Space direction="vertical" size={16} style={{ width: "100%" }}>
              <Row gutter={[12, 12]}>
                <Col xs={24} md={6}><Text strong>Method</Text><div>{adminSelectedLog.method || "-"}</div></Col>
                <Col xs={24} md={10}><Text strong>Endpoint</Text><div>{adminSelectedLog.endpoint || "-"}</div></Col>
                <Col xs={24} md={4}><Text strong>Status</Text><div>{adminSelectedLog.status_code || "-"}</div></Col>
                <Col xs={24} md={4}><Text strong>Time</Text><div>{adminSelectedLog.createdAt ? moment(adminSelectedLog.createdAt).format("YYYY-MM-DD HH:mm:ss") : "-"}</div></Col>
              </Row>

              <Card size="small" title="Message">
                <Text>{adminSelectedLog.message || "-"}</Text>
              </Card>

              {Number(adminSelectedLog.status_code) === 304 && isEffectivelyEmptyJson(adminSelectedLog.response_body || adminSelectedLog.responseBody) ? (
                <div style={{ padding: "12px 14px", border: "1px solid #cfe7ef", borderRadius: 8, background: "#f7fbfd", color: "#406273" }}>
                  304 Not Modified responses do not include a response body. Check the endpoint headers or upstream cache behavior instead.
                </div>
              ) : null}

              <Card size="small" title="Request Body JSON">
                {renderJsonBodyBlock(adminSelectedLog.request_body ?? adminSelectedLog.requestBody ?? null, "No request body captured.")}
              </Card>

              <Card size="small" title="Request Context">
                {renderJsonBodyBlock(adminSelectedLog.request_context ?? adminSelectedLog.requestContext ?? null, "No request context captured.")}
              </Card>

              <Card size="small" title="Response Body JSON">
                {renderJsonBodyBlock(
                  adminSelectedLog.response_body ?? adminSelectedLog.responseBody ?? null,
                  Number(adminSelectedLog.status_code) === 304 ? "No response body captured for 304 Not Modified." : "No response body captured."
                )}
              </Card>

              <Card size="small" title="Response Headers">
                {renderJsonBodyBlock(adminSelectedLog.response_headers ?? adminSelectedLog.responseHeaders ?? null, "No response headers captured.")}
              </Card>
            </Space>
          ) : null}
        </Modal>
      </>
    );
  };

  const renderActive = () => {
    if (activeKey === "overview") return renderOverview();
    if (activeKey === "api-keys") return renderApiKeys();
    if (activeKey === "usage") return renderUsage();
    if (activeKey === "billing") return renderBilling();
    if (activeKey === "profile") return renderProfile();
    if (activeKey === "admin-usage") return renderAdminUsage();
    return renderDocs();
  };

  return (
    <Layout className="portal-layout">
      <Header className="portal-header">
        <div className="header-brand-wrap">
          <img src={BRAND_LOGO_PATH} alt="OneChain" className="header-brand-logo" />
        </div>
        <Dropdown
          menu={{ items: headerMenuItems }}
          open={headerMenuOpen}
          onOpenChange={setHeaderMenuOpen}
          placement="bottomRight"
          trigger={["click"]}
        >
          <div className="header-user-block header-user-clickable">
            <div className="header-user-name">{currentUser?.username || "-"}</div>
            <div className="header-user-email">{myProfile?.email || currentUser?.email || ""}</div>
          </div>
        </Dropdown>
      </Header>
      <Layout className="portal-main-layout">
        <Sider width={230} className="portal-sider" breakpoint="lg" collapsedWidth={100} theme="dark">
          <Menu
            theme="dark"
            mode="inline"
            selectedKeys={[activeKey]}
            items={menuItems}
            onClick={({ key }) => setActiveKey(key)}
            className="portal-menu"
          />
        </Sider>
        <Layout>
          <Content className="portal-content">
            <div key={activeKey} className="animated-tab-content">
              {renderActive()}
            </div>
          </Content>
        </Layout>
      </Layout>
      <Modal
        title="Upgrade to Pro"
        open={upgradeModalOpen}
        onCancel={() => setUpgradeModalOpen(false)}
        onOk={() => setUpgradeModalOpen(false)}
        okText="Understood"
        cancelButtonProps={{ style: { display: "none" } }}
      >
        <p>Contact the admin for detail.</p>
      </Modal>
    </Layout>
  );
};

export default PortalShell;
